/**
 * LocationStep — the Create Workshop wizard's first step (BON-203 round 8):
 * Region + Venue in one screen, always a real stop. Each field renders per its
 * mode: `fixed` → read-only text; otherwise an enabled selector (pre-seeded for
 * `prefill`). Region scopes the venue list; changing region (when editable)
 * clears the venue. The reverse also holds: picking a venue adopts the region
 * that owns it, so a region-less form never strands a chosen venue. Extracted
 * from workshopCreateSteps to keep that file under the size cap.
 *
 * A viewer with nowhere to create is shown the reason instead of an empty Venue
 * dropdown: the step is a dead end either way, and only one of those says so.
 * A failed lookup is a dead end too, but a different one — saying "you have no
 * permission" when a request simply errored sends the reader to the wrong
 * person for a fix.
 */
import type { CSSProperties } from 'react';

import type { RegionNode } from '../../../../api/regions';
import { ControlBlock } from '../../primitives/ControlBlock';
import { FixedField } from '../../primitives/FixedField';
import { LocationRegionField } from '../../primitives/LocationRegionField';
import { fieldStyle } from '../../venues/venueCreateFields';

import { ConfirmationBanner } from './ConfirmationBanner';
import type { WorkshopCreateForm } from './useWorkshopCreateForm';
import { selectableRegionForVenue } from './workshopCreateRegions';
import type { VenueOption } from './workshopCreateSteps';

const COL: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 14 };

const NOTE: CSSProperties = {
  fontSize: 10.5,
  color: 'var(--nb-subtle)',
  lineHeight: 1.5,
  margin: 0,
};

const NO_REGIONS_NOTE =
  'You do not have permission to create workshops in any region. Ask an administrator to assign you one.';

const LOAD_FAILED_NOTE =
  'We could not load your regions, so there is nothing to choose from yet. Close this and try again.';

export function LocationStep({
  form,
  regions,
  regionsLoading,
  regionsFailed,
  venues,
  regionLabel,
  venueLabel,
}: {
  form: WorkshopCreateForm;
  regions: RegionNode[];
  /** True while the writable regions are still being resolved. */
  regionsLoading: boolean;
  /** True when resolving them failed — an empty list means unknown, not none. */
  regionsFailed: boolean;
  venues: VenueOption[];
  regionLabel: string;
  venueLabel: string;
}) {
  const { regionId, regionMode, venueMode, setRegion, setVenue, draft, fieldError, confirmation } =
    form;
  // Only an editable Region field can be empty of options. A fixed one was set
  // by the launching page and stands on its own, so the venue-detail flow is
  // untouched by any of this.
  const editable = regionMode !== 'fixed';
  const nowhereToCreate = editable && !regionsLoading && regions.length === 0;
  const note = regionsFailed ? LOAD_FAILED_NOTE : NO_REGIONS_NOTE;
  return (
    <div style={COL}>
      <ConfirmationBanner message={confirmation} />
      <LocationRegionField
        mode={regionMode}
        regions={regions}
        loading={regionsLoading}
        failed={regionsFailed}
        value={regionId}
        label={regionLabel}
        error={fieldError('regionId')}
        onChange={setRegion}
      />
      {nowhereToCreate ? null : venueMode === 'fixed' ? (
        <FixedField label="Venue" value={venueLabel} ariaLabel="Venue (fixed)" />
      ) : (
        <ControlBlock label="Venue" error={fieldError('venueId')}>
          <select
            aria-label="Venue"
            style={fieldStyle(fieldError('venueId') ? 'error' : undefined)}
            value={draft.schedule.venueId}
            onChange={(e) =>
              setVenue(e.target.value, selectableRegionForVenue(venues, e.target.value, regions))
            }
          >
            <option value="">Select a venue…</option>
            {venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </ControlBlock>
      )}
      <p style={NOTE}>{nowhereToCreate ? note : 'Confirm where this workshop runs.'}</p>
    </div>
  );
}
